import { BusinessSettings } from '../types';
import { auth } from '../lib/firebase';
import { syncStateToCloud } from './cloudSyncService';

export const DEFAULT_BUSINESS_SETTINGS: BusinessSettings = {
  senderName: '',
  companyName: '',
  email: '',
  phone: '',
  address: '',
  taxNumber: '',
  registrationNumber: '',
  website: '',
  socialHandles: '',
  logoUrl: '',
  paymentLink: '',
  bankDetails: '',
  jurisdiction: 'ZA',
  currencySymbol: 'R',
  currencyCode: 'ZAR',
  vatRate: 15,
  taxAuthorityLabel: 'SARS (South Africa)',
  taxNumberLabel: 'VAT Reg / Tax ID #',
  invoiceTypeLabel: 'Tax Invoice',
  fiscalYearEndMonth: 2,
  invoiceNotes: ''
};

export function getBusinessSettings(): BusinessSettings {
  try {
    const saved = localStorage.getItem('solostack_business_settings');
    if (saved) {
      const parsed = JSON.parse(saved);
      // Clean up legacy defaults leftover from earlier app versions
      if (parsed.companyName === 'Azura Seraph Pty Ltd') parsed.companyName = '';
      if (parsed.senderName === 'AzuraOS Founder') parsed.senderName = '';
      if (parsed.email === 'accounts@azuraos.app') parsed.email = '';
      if (parsed.phone === '+27 21 555 0192') parsed.phone = '';
      if (parsed.address === 'Cape Town, South Africa') parsed.address = '';
      if (parsed.website === 'azuraos.app') parsed.website = '';
      if (parsed.socialHandles === '@azuraos') parsed.socialHandles = '';
      if (parsed.bankDetails && parsed.bankDetails.includes('Azura Seraph')) parsed.bankDetails = '';

      return { ...DEFAULT_BUSINESS_SETTINGS, ...parsed };
    }
  } catch (e) {
    console.error('Failed to parse business settings from localStorage:', e);
  }
  return DEFAULT_BUSINESS_SETTINGS;
}

export function saveBusinessSettings(settings: BusinessSettings): void {
  try {
    localStorage.setItem('solostack_business_settings', JSON.stringify(settings));
    window.dispatchEvent(new CustomEvent('businessSettingsUpdated', { detail: settings }));
    if (auth.currentUser?.uid) {
      syncStateToCloud(auth.currentUser.uid, undefined, settings).catch((err) => {
        console.warn('Could not immediately sync business settings to cloud:', err);
      });
    }
  } catch (e) {
    console.error('Failed to save business settings to localStorage:', e);
  }
}

export interface ParsedBankDetails {
  bankName: string;
  accountHolder: string;
  accountNumber: string;
  branchCode: string;
  accountType: string;
  raw: string;
}

export function parseBankDetails(rawText?: string, defaultHolder: string = ''): ParsedBankDetails {
  if (!rawText || !rawText.trim()) {
    return {
      bankName: '',
      accountHolder: defaultHolder,
      accountNumber: '',
      branchCode: '',
      accountType: 'Commercial Cheque Account',
      raw: ''
    };
  }

  const text = rawText.trim();
  
  // Extract patterns commonly used in SA and global bank wire strings
  const bankMatch = text.match(/(?:Bank(?:\s*Name)?|Institution):\s*([^\n|;,]+)/i);
  const holderMatch = text.match(/(?:Account\s*Name|Account\s*Holder|Name|Holder):\s*([^\n|;,]+)/i);
  const accNumMatch = text.match(/(?:Account(?:\s*#|\s*No|\s*Number)?|Acc(?:\s*#|\s*No)?):\s*([^\n|;,]+)/i);
  const branchMatch = text.match(/(?:Branch(?:\s*Code)?|Sort(?:\s*Code)?):\s*([^\n|;,]+)/i);
  const typeMatch = text.match(/(?:Account\s*Type|Type):\s*([^\n|;,]+)/i);

  return {
    bankName: bankMatch ? bankMatch[1].trim() : '',
    accountHolder: holderMatch ? holderMatch[1].trim() : defaultHolder,
    accountNumber: accNumMatch ? accNumMatch[1].trim() : '',
    branchCode: branchMatch ? branchMatch[1].trim() : '',
    accountType: typeMatch ? typeMatch[1].trim() : 'Commercial Cheque Account',
    raw: text
  };
}

export function formatBankDetails(parts: {
  bankName?: string;
  accountHolder?: string;
  accountNumber?: string;
  branchCode?: string;
  accountType?: string;
}): string {
  const items: string[] = [];
  if (parts.bankName) items.push(`Bank: ${parts.bankName.trim()}`);
  if (parts.accountHolder) items.push(`Account Name: ${parts.accountHolder.trim()}`);
  if (parts.accountNumber) items.push(`Account #: ${parts.accountNumber.trim()}`);
  if (parts.branchCode) items.push(`Branch Code: ${parts.branchCode.trim()}`);
  if (parts.accountType) items.push(`Type: ${parts.accountType.trim()}`);
  return items.join(' | ');
}

